import React from 'react';
import { useSnackbar } from 'notistack';
import { Button } from '@mui/material';

import { DataAPIContext, GET_ONE, GET_LIST } from '../api';
import { usePageVisibility } from '../components/usePageVisibility';

const POLL_INTERVAL_COUNT = 15000;
const POLL_INTERVAL_STATUS = 15000;

export const BackgroundTaskContext = React.createContext({
    activeCount: 0,
    taskCache: {},
    recentTasks: [],
    popoverOpen: false,
    setPopoverOpen: () => {},
    dialogOpen: false,
    setDialogOpen: () => {},
    fetchRecentTasks: () => {},
});

/** Imperative handle for bg task UI actions — avoids subscribing whole pages to polling context. */
export const backgroundTaskActionsRef = { current: null };

const BackgroundTaskProvider = ({ children }) => {
    const dataAPI = React.useContext(DataAPIContext);
    const isPageVisible = usePageVisibility();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const [activeCount, setActiveCount] = React.useState(0);
    const [taskCache, setTaskCache] = React.useState({});
    const [recentTasks, setRecentTasks] = React.useState([]);
    const [oldestTs, setOldestTs] = React.useState(null);
    const [popoverOpen, setPopoverOpen] = React.useState(false);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [expandTaskId, setExpandTaskId] = React.useState(null);

    const countIntervalRef = React.useRef(null);
    const statusIntervalRef = React.useRef(null);

    // Task completion callback registry
    const taskCallbacksRef = React.useRef({});
    const registerTaskCallback = React.useCallback((taskId, callback) => {
        taskCallbacksRef.current[taskId] = callback;
    }, []);
    const unregisterTaskCallback = React.useCallback((taskId) => {
        delete taskCallbacksRef.current[taskId];
    }, []);

    // Loop 1: Poll active count (15s)
    React.useEffect(() => {
        const pollCount = () => {
            dataAPI(GET_ONE, 'bg_tasks', { urlSuffix: 'count/' }).then((res) => {
                if (res && res.statusCode === 200 && res.data) {
                    setActiveCount(res.data.active || 0);
                }
            }).catch(() => {});
        };

        const start = () => {
            pollCount();
            countIntervalRef.current = setInterval(pollCount, POLL_INTERVAL_COUNT);
        };
        const stop = () => {
            if (countIntervalRef.current) {
                clearInterval(countIntervalRef.current);
                countIntervalRef.current = null;
            }
        };

        if (isPageVisible) {
            start();
        } else {
            stop();
        }
        return stop;
    }, [isPageVisible, dataAPI]);

    // Loop 2: Poll status changes (15s, staggered)
    React.useEffect(() => {
        const pollStatus = async () => {
            try {
                let params;
                if (!oldestTs) {
                    // Initial fetch: get active tasks
                    params = {
                        pagination: { page: 1, perPage: 50 },
                        sort: { field: 'created_ts', order: 'DESC' },
                        filter: { state: '0,1,5' },
                    };
                } else {
                    // Subsequent: fetch changes since last poll
                    params = {
                        pagination: { page: 1, perPage: 50 },
                        sort: { field: 'updated_ts', order: 'DESC' },
                        filter: { updated_since: oldestTs },
                    };
                }

                const res = await dataAPI(GET_LIST, 'bg_tasks', params);
                if (!res || res.statusCode !== 200 || !res.data) return;

                const tasks = res.data.results || res.data || [];
                if (tasks.length === 0 && !oldestTs) {
                    setOldestTs(new Date().toISOString());
                    return;
                }

                // Diff with cache and show toasts / fire callbacks for state transitions
                const newCache = { ...taskCache };
                for (const task of tasks) {
                    const prev = newCache[task.id];
                    const isTerminal = task.state === 2 || task.state === 3 || task.state === 4;
                    const stateChanged = prev && prev.state !== task.state;

                    if (stateChanged) {
                        showTransitionToast(task, prev.state);
                    }

                    // Fire registered callback when task reaches terminal state —
                    // either via a state transition or first appearance as terminal
                    // (task completed before the first poll picked it up)
                    if (isTerminal && (stateChanged || !prev)) {
                        const cb = taskCallbacksRef.current[task.id];
                        if (cb) {
                            try { cb(task); } catch (e) { console.error('Task callback error:', e); }
                            delete taskCallbacksRef.current[task.id];
                        }
                    }

                    newCache[task.id] = task;
                }
                setTaskCache(newCache);

                // Update oldest timestamp
                if (tasks.length > 0) {
                    const timestamps = tasks
                        .map(t => t.updated_ts || t.created_ts)
                        .filter(Boolean);
                    if (timestamps.length > 0) {
                        const latest = timestamps.sort().pop();
                        setOldestTs(latest);
                    }
                } else if (!oldestTs) {
                    setOldestTs(new Date().toISOString());
                }
            } catch (e) {
                // Silently ignore polling errors
            }
        };

        const start = () => {
            // Stagger by half the interval from the count poll
            const delay = Math.round(POLL_INTERVAL_STATUS / 2);
            const timer = setTimeout(() => {
                pollStatus();
                statusIntervalRef.current = setInterval(pollStatus, POLL_INTERVAL_STATUS);
            }, delay);
            return () => {
                clearTimeout(timer);
                if (statusIntervalRef.current) {
                    clearInterval(statusIntervalRef.current);
                    statusIntervalRef.current = null;
                }
            };
        };
        const stop = () => {
            if (statusIntervalRef.current) {
                clearInterval(statusIntervalRef.current);
                statusIntervalRef.current = null;
            }
        };

        if (isPageVisible) {
            const cleanup = start();
            return () => {
                stop();
                cleanup && cleanup();
            };
        } else {
            stop();
            return stop;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPageVisible, dataAPI, oldestTs]);

    const showTransitionToast = React.useCallback((task, prevState) => {
        const label = task.task_label || task.task_type;
        if (task.state === 2) {
            enqueueSnackbar(`${label} completed`, {
                variant: 'success',
                action: (key) => (
                    <Button color="inherit" size="small" onClick={() => { closeSnackbar(key); setPopoverOpen(true); }}>
                        View
                    </Button>
                ),
            });
        } else if (task.state === 3) {
            enqueueSnackbar(`${label} failed: ${task.error_message || 'unknown error'}`, {
                variant: 'error',
                autoHideDuration: 5000,
                action: (key) => (
                    <Button color="inherit" size="small" onClick={() => { closeSnackbar(key); setPopoverOpen(true); }}>
                        View
                    </Button>
                ),
            });
        } else if (task.state === 4) {
            enqueueSnackbar(`${label} cancelled`, { variant: 'warning' });
        }
    }, [enqueueSnackbar, closeSnackbar]);

    const fetchRecentTasks = React.useCallback(() => {
        dataAPI(GET_LIST, 'bg_tasks', {
            pagination: { page: 1, perPage: 10 },
            sort: { field: 'created_ts', order: 'DESC' },
        }).then((res) => {
            if (res && res.statusCode === 200 && res.data) {
                setRecentTasks(res.data.results || res.data || []);
            }
        }).catch(() => {});
    }, [dataAPI]);

    const openDialogForTask = React.useCallback((taskId) => {
        setPopoverOpen(false);
        setExpandTaskId(taskId);
        setDialogOpen(true);
    }, []);

    backgroundTaskActionsRef.current = { openDialogForTask };

    const value = React.useMemo(() => ({
        activeCount,
        taskCache,
        recentTasks,
        popoverOpen,
        setPopoverOpen,
        dialogOpen,
        setDialogOpen,
        expandTaskId,
        setExpandTaskId,
        openDialogForTask,
        fetchRecentTasks,
        registerTaskCallback,
        unregisterTaskCallback,
    }), [activeCount, taskCache, recentTasks, popoverOpen, dialogOpen, expandTaskId, openDialogForTask, fetchRecentTasks, registerTaskCallback, unregisterTaskCallback]);

    return (
        <BackgroundTaskContext.Provider value={value}>
            {children}
        </BackgroundTaskContext.Provider>
    );
};

export default BackgroundTaskProvider;
